import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import { CustomIcon } from 'modules/components/layer00';
import React, { ReactNode } from 'react';
import { theme } from 'theme';
import {
  IFieldName,
  IPlanMetaDetail
} from './types';
import { appFormUtil } from 'modules/common';
import { SelectPlanVersionField } from 'modules/components/layer04';
import QRCode from 'react-qr-code';



const fieldNames: IFieldName[] = [
  'fileSize',
  'createdTimestamp',
  'updatedTimestamp',
  'fileType',
  'uploadedBy'
];

const fieldName2Label: Record<IFieldName, string> = {
  fileSize: 'File Size',
  createdTimestamp: 'Created At',
  updatedTimestamp: 'Updated At',
  fileType: 'File Type',
  title: 'Title',
  uploadedBy: 'Uploaded By'
};

const fieldName2Icon: Record<IFieldName, () => ReactNode> = {
  fileSize: () => <CustomIcon.large.FC icon='DOC' stroke={theme.palette.primary.main} />,
  createdTimestamp: () => <CustomIcon.large.FC icon='CALENDARALT' stroke={theme.palette.primary.main} />,
  updatedTimestamp: () => <CustomIcon.large.FC icon='CALENDARALT' stroke={theme.palette.primary.main} />,
  title: () => <CustomIcon.large.FC icon='DOC' stroke={theme.palette.primary.main} />,
  fileType: () => <CustomIcon.large.FC icon='FILE' stroke={theme.palette.primary.main} />,
  uploadedBy: () => <CustomIcon.large.FC icon='CREATOR' stroke={theme.palette.primary.main} />
};



export type PlanMetaDetailSectionProps = {
  expanded: boolean;
  showDetail: React.Dispatch<React.SetStateAction<boolean>>;
  setIsDeleteVisible: React.Dispatch<React.SetStateAction<boolean>>;
  editPlan: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
  fieldName2Value: Record<IFieldName, string>;
  addVersion: React.Dispatch<React.SetStateAction<boolean>>;
  form: appFormUtil.InferReturn<IPlanMetaDetail>;
};

/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
export const FC = (props: PlanMetaDetailSectionProps) => {
  const theme = useTheme();

  const handleDownload = (url: string) => {
    window.open(url, '_blank');
  };
  if (props.expanded) {
    return (
      <>
        <Box
          position='fixed'
          top={0}
          right={0}
          bgcolor={theme.palette.background.default}
          height='100%'
          width='400px'
          paddingTop='1rem'
          paddingX='1rem'
        >
          <Stack
            direction='row'
            overflow='clip'
            alignItems='center'
            justifyContent='center'
            columnGap='1rem'
          >
            <IconButton
              onClick={() => {
                props.showDetail(() => false);
              }}
              sx={{
                border: `1px solid ${theme.palette.primary.main}`,
                marginRight: '20px'
              }}
            >
              <CustomIcon.medium.FC
                icon='CLOSE'
                stroke='primary'
              />
            </IconButton>
            <Typography
              flex='1 1 0'
              variant='modalTitle'
              textOverflow='ellipsis'
            >
              {props.title}
            </Typography>
            <IconButton
              onClick={() => props.editPlan(() => true)}
            >
              <CustomIcon.small.FC
                icon='PENCIL'
                stroke={theme.palette.primary.main}
              />
            </IconButton>
          </Stack>
          {/* FieldNames */}
          <Box
            display='flex'
            flexDirection='column'
            rowGap='1rem'
          >
            {fieldNames.map((node, i) => {
              const Icon = fieldName2Icon[node];
              const label = fieldName2Label[node];
              return (
                <Stack
                  key={i}
                  direction='row'
                  columnGap='.5rem'
                  alignItems='center'
                >
                  <Icon />

                  <Typography
                    variant='formLabel'
                  >
                    {label}
                  </Typography>
                  <Typography
                    variant='formInput'
                  >
                    {props.fieldName2Value[node]}
                  </Typography>

                </Stack>
              );
            })}
          </Box>
          <Stack
            direction='column'
            rowGap='.5rem'
          >
            <SelectPlanVersionField.FC
              form={props.form}
              fieldName='selectedFileId'
            />
            <Button
              onClick={() => props.addVersion(() => true)}
              startIcon={(
                <CustomIcon.large.FC
                  icon='PLUS'
                  stroke={theme.palette.primary.contrastText}
                />
              )}
              variant='contained'
            >
              Add Version
            </Button>
            <Button
              variant='contained'
              color='primary'
              startIcon={(
                <CustomIcon.large.FC
                  icon='DOWLOAD'
                  stroke={theme.palette.primary.contrastText}
                />
              )}
              onClick={() => handleDownload(props.form.formState?.versions.find((node) => node.id === props.form.formState.selectedFileId)?.url ?? '')}
            >
              Download
            </Button>
            <Button
              variant='contained'
              color='error'
              startIcon={(
                <CustomIcon.large.FC
                  icon='DELETE'
                  stroke={theme.palette.primary.contrastText}
                />
              )}
              onClick={() => props.setIsDeleteVisible(() => true)}
            >
              Delete Plan
            </Button>
          </Stack>

          {/* DOWNLOADS */}
          <Stack
            direction='column'
            padding='2.5rem'
            rowGap='1.5rem'
          >
            <Box
              margin='auto'
            >
              <QRCode
                value={props.form.formState?.versions.find((node) => node.id === props.form.formState.selectedFileId)?.url ?? ''}
              />
            </Box>
            <Typography
              variant='modalTitle'
              textAlign='center'
              sx={{
                wordBreak: 'break-word'
              }}
            >
              Scan QR code to download
            </Typography>

            <Typography
              variant='body1'
              textAlign='center'
              sx={{
                marginTop: '-1rem',
                wordBreak: 'break-word'
              }}
            >
              This code will let you download plan on any device.
            </Typography>
          </Stack>

        </Box>
      </>
    );
  }
  return (

    <>

      <Box
        position='fixed'
        right={15}
        top={15}
        zIndex={1000}

      >
        <IconButton
          sx={{
            width: '40px',
            height: '40px',
            zIndex: 1000,
            bgcolor: theme.palette.primary.main,
            border: `1px solid ${theme.palette.primary.main}`
          }}
          onClick={() => {
            props.showDetail(() => true);
          }}
        >
          <CustomIcon.large.FC icon='EDIT' stroke={theme.palette.primary.contrastText} />
        </IconButton>
      </Box>
    </>
  );
};
